// Parse an uploaded Special SK schedule (Excel/CSV or PDF) into locked entries.
// Real-world format (one ROW per satsang):
//   S.No. | Date | Day | From (Area) | Satsang Karta Name | Contact No. | Time | Satsang Ghar
// Each SK typically covers 2 satsangs the same day (9:30 AM + 1:00 PM) at two ghars.
// Also still supports the simpler 1-row format: Date | SK Name | Morning Ghar | Noon Ghar | City.
import * as XLSX from "xlsx";
import { GHARS } from "../data/rssbData";
import { hinglishToHindi } from "./translit";

const uid = () => Math.random().toString(36).slice(2, 9);

// English honorific -> Hindi (so transliterated names read naturally in Devanagari).
const HONORIFICS = {
  smt: "श्रीमती", "smt.": "श्रीमती", mrs: "श्रीमती", "mrs.": "श्रीमती",
  shri: "श्री", "shri.": "श्री", sri: "श्री", sh: "श्री", "sh.": "श्री",
  mr: "श्री", "mr.": "श्री", ms: "सुश्री", "ms.": "सुश्री",
  km: "कुमारी", "km.": "कुमारी", kum: "कुमारी", "kum.": "कुमारी",
  dr: "डॉ॰", "dr.": "डॉ॰",
};

async function toHindiName(raw) {
  if (!raw) return "";
  const pre = String(raw)
    .trim()
    .split(/\s+/)
    .map((t) => HONORIFICS[t.toLowerCase()] || t)
    .join(" ");
  return await hinglishToHindi(pre);
}

// Transliterate SK names & cities to Hindi; leave already-Devanagari text untouched.
async function transliterateEntries(entries) {
  await Promise.all(
    entries.map(async (e) => {
      e.skName = await toHindiName(e.skName);
      if (e.fromCity) e.fromCity = await hinglishToHindi(e.fromCity);
    })
  );
  return entries;
}

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9\u0900-\u097F]/g, "");

// Levenshtein distance (small strings).
function lev(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[m][n];
}

// Fuzzy-match a place name (English or Hindi) to a ghar id.
export function matchGhar(text) {
  if (!text) return null;
  const t = norm(text);
  if (!t) return null;
  let best = null, bestScore = -1;
  GHARS.forEach((g) => {
    const en = norm(g.en);
    const hi = norm(g.hi);
    let score = 0;
    if (t === en || t === hi) score = 1;
    else if (en.includes(t) || t.includes(en) || hi.includes(t) || t.includes(hi)) score = 0.9;
    else {
      const d = Math.min(lev(t, en), hi ? lev(t, hi) : 99);
      const ref = Math.max(t.length, en.length);
      score = 1 - d / ref; // similarity
    }
    if (score > bestScore) { bestScore = score; best = g; }
  });
  return bestScore >= 0.7 ? best.id : null;
}

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

function normalizeDate(val, year, month) {
  if (val == null || val === "") return null;
  if (typeof val === "number") {
    const d = XLSX.SSF ? XLSX.SSF.parse_date_code(val) : null;
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  const s = String(val).trim();
  // "04 Oct 2026" / "4 October 2026"
  let m = s.match(/(\d{1,2})\s*[-\s]\s*([A-Za-z]{3,})\s*[-\s,]*\s*(\d{4})/);
  if (m) {
    const mon = MONTHS[m[2].slice(0, 3).toLowerCase()];
    if (mon != null) return `${m[3]}-${String(mon + 1).padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  // "04/10/2026" or "04-10-2026"
  m = s.match(/(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})/);
  if (m) {
    let yy = m[3]; if (yy.length === 2) yy = "20" + yy;
    return `${yy}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  // bare day number
  const dayOnly = s.match(/^(\d{1,2})$/);
  if (dayOnly) return `${year}-${String(month + 1).padStart(2, "0")}-${dayOnly[1].padStart(2, "0")}`;
  return null;
}

const cleanMobile = (v) => {
  const s = String(v || "").trim();
  if (!s || s === "—" || s === "-") return "";
  return s;
};

// Find the header row and map logical fields -> column index.
function resolveColumns(rows) {
  for (let i = 0; i < Math.min(rows.length, 8); i++) {
    const cells = rows[i].map((c) => String(c || "").toLowerCase().trim());
    const find = (...keys) => cells.findIndex((c) => keys.some((k) => c.includes(k)));
    const dateCol = find("date", "तारीख", "दिनांक");
    const nameCol = find("karta name", "sk name", "satsang karta", "preacher", "नाम", "कर्ता");
    const gharCol = find("satsang ghar", "ghar", "location", "place", "घर", "स्थान");
    if (dateCol !== -1 && nameCol !== -1) {
      return {
        headerRow: i,
        dateCol,
        nameCol,
        gharCol,
        timeCol: find("time", "समय"),
        mobileCol: find("contact", "mobile", "phone", "मोबाइल", "फोन", "संपर्क"),
        areaCol: find("from", "area", "city", "शहर", "क्षेत्र"),
        morningCol: find("morning"),
        noonCol: find("noon"),
      };
    }
  }
  return null;
}

export async function parseExcel(file, year, month) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const cols = resolveColumns(rows);
  if (!cols) return [];

  // Legacy single-row format (explicit Morning/Noon columns).
  if (cols.morningCol !== -1 && cols.noonCol !== -1) {
    const entries = [];
    rows.slice(cols.headerRow + 1).forEach((r) => {
      const dateIso = normalizeDate(r[cols.dateCol], year, month);
      const skName = String(r[cols.nameCol] || "").trim();
      if (!dateIso || !skName) return;
      entries.push({
        id: uid(), dateIso, skName,
        skMobile: cleanMobile(cols.mobileCol !== -1 ? r[cols.mobileCol] : ""),
        fromCity: cols.areaCol !== -1 ? String(r[cols.areaCol] || "").trim() : "",
        morningGharId: matchGhar(r[cols.morningCol]) || "",
        morningGharText: String(r[cols.morningCol] || "").trim(),
        noonGharId: matchGhar(r[cols.noonCol]) || "",
        noonGharText: String(r[cols.noonCol] || "").trim(),
      });
    });
    return await transliterateEntries(entries);
  }

  // Row-per-satsang format: group two rows (same date+name) into morning + noon.
  const groups = new Map();
  rows.slice(cols.headerRow + 1).forEach((r) => {
    const dateIso = normalizeDate(r[cols.dateCol], year, month);
    const skName = String(r[cols.nameCol] || "").trim();
    if (!dateIso || !skName) return;
    const ghar = cols.gharCol !== -1 ? String(r[cols.gharCol] || "").trim() : "";
    if (!ghar) return;
    const time = cols.timeCol !== -1 ? String(r[cols.timeCol] || "").trim() : "";
    const key = `${dateIso}|${skName}`;
    if (!groups.has(key)) {
      groups.set(key, {
        id: uid(), dateIso, skName,
        skMobile: cleanMobile(cols.mobileCol !== -1 ? r[cols.mobileCol] : ""),
        fromCity: cols.areaCol !== -1 ? String(r[cols.areaCol] || "").trim() : "",
        morningGharId: "", morningGharText: "", noonGharId: "", noonGharText: "",
      });
    }
    const g = groups.get(key);
    const gharId = matchGhar(ghar) || "";
    const isNoon = /pm|1:00|13:|दोपहर|noon/i.test(time) || (!/am|9:|morning|प्रात/i.test(time) && g.morningGharText);
    if (isNoon && !g.noonGharText) {
      g.noonGharId = gharId; g.noonGharText = ghar;
    } else if (!g.morningGharText) {
      g.morningGharId = gharId; g.morningGharText = ghar;
    } else if (!g.noonGharText) {
      g.noonGharId = gharId; g.noonGharText = ghar;
    }
  });
  return await transliterateEntries([...groups.values()]);
}

// PDF: extract lines, group by date+name like the Excel path.
export async function parsePdf(file, year, month) {
  const pdfjs = await import("pdfjs-dist/build/pdf");
  const worker = await import("pdfjs-dist/build/pdf.worker.entry");
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const lines = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const byY = {};
    content.items.forEach((it) => {
      const y = Math.round(it.transform[5]);
      byY[y] = byY[y] || [];
      byY[y].push(it.str);
    });
    Object.keys(byY).sort((a, b) => b - a).forEach((y) => lines.push(byY[y].join(" ").trim()));
  }
  const entries = [];
  lines.forEach((line) => {
    const dateIso = normalizeDate(line, year, month);
    if (!dateIso) return;
    entries.push({
      id: uid(), dateIso,
      skName: line.replace(/\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4}/, "").trim().slice(0, 60),
      skMobile: "",
      morningGharId: matchGhar(line) || "", morningGharText: "",
      noonGharId: "", noonGharText: "", fromCity: "",
    });
  });
  return entries;
}

export async function parseSpecialFile(file, year, month) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf")) return parsePdf(file, year, month);
  return parseExcel(file, year, month);
}

export { uid };

// Re-transliterate any stored entries that still contain Latin letters (e.g. saved
// before the Hindi-name fix). Returns { entries, changed }.
export async function ensureHindiSpecials(entries) {
  let changed = false;
  const out = [];
  for (const e of entries || []) {
    const hasLatin = /[A-Za-z]/.test(e.skName || "") || /[A-Za-z]/.test(e.fromCity || "");
    if (hasLatin) {
      changed = true;
      out.push({
        ...e,
        skName: await toHindiName(e.skName),
        fromCity: e.fromCity ? await hinglishToHindi(e.fromCity) : e.fromCity,
      });
    } else {
      out.push(e);
    }
  }
  return { entries: out, changed };
}
